import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Target, Lightbulb, TrendingUp } from "lucide-react";
import { Header } from "../components/portfolio/Header";
import { Footer } from "../components/portfolio/Footer";
import { PROJECTS, PROFILE } from "../data";

export default function ProjectPage() {
  const { id } = useParams();
  const project = PROJECTS.find((p) => p.id === id);
  const index = PROJECTS.findIndex((p) => p.id === id);
  const next = PROJECTS[(index + 1) % PROJECTS.length];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (project) document.title = `${project.title} — ${PROFILE.name}`;
  }, [project]);

  if (!project) {
    return (
      <div data-testid="project-not-found" className="bg-white min-h-screen">
        <Header />
        <div className="mx-auto max-w-3xl section-x pt-40 pb-24">
          <h1 className="font-serif text-3xl text-navy">Project not found</h1>
          <Link to="/" className="text-berry mt-4 inline-block">Return home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = project.icon;

  return (
    <div data-testid="project-page" className="bg-white min-h-screen">
      <Header />

      <section className="relative bg-navy text-white overflow-hidden">
        <img src={project.image} alt={project.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy/80 to-navy/50" />
        <div className="relative mx-auto max-w-5xl section-x pt-36 pb-16 lg:pt-44 lg:pb-20">
          <Link
            to="/"
            data-testid="project-back"
            onClick={() => setTimeout(() => document.getElementById("work")?.scrollIntoView(), 100)}
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-berry-light transition-colors duration-200 mb-10"
          >
            <ArrowLeft size={16} /> Back to work
          </Link>
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-white/10 border border-white/15 mb-8">
            <Icon size={26} />
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight max-w-3xl">
            {project.title}
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">{project.overview}</p>
          <div className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-4 text-sm">
            <div>
              <p className="label-caps text-white/40 mb-1">Role</p>
              <p className="text-white/90">{project.role}</p>
            </div>
            <div>
              <p className="label-caps text-white/40 mb-1">Timeframe</p>
              <p className="text-white/90">{project.timeframe}</p>
            </div>
            <div>
              <p className="label-caps text-white/40 mb-1">Focus</p>
              <p className="text-white/90">{project.tags.join(" · ")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl section-x py-20 lg:py-28 grid lg:grid-cols-3 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="lg:col-span-3"
        >
          <div className="flex items-center gap-3 mb-5">
            <Target size={20} className="text-berry" />
            <h2 className="font-serif text-2xl sm:text-3xl text-navy">The challenge</h2>
          </div>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">{project.challenge}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="lg:col-span-3"
        >
          <div className="flex items-center gap-3 mb-6">
            <Lightbulb size={20} className="text-berry" />
            <h2 className="font-serif text-2xl sm:text-3xl text-navy">The approach</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {project.approach.map((a, i) => (
              <div key={i} data-testid={`approach-${i}`} className="flex gap-4 p-6 border border-slate-200 rounded-sm bg-slate-50/50">
                <span className="font-serif text-berry text-xl leading-none">0{i + 1}</span>
                <p className="text-sm text-slate-600 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="lg:col-span-3"
        >
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp size={20} className="text-berry" />
            <h2 className="font-serif text-2xl sm:text-3xl text-navy">The outcome</h2>
          </div>
          <ul className="space-y-4 max-w-3xl">
            {project.outcomes.map((o, i) => (
              <li key={i} data-testid={`outcome-${i}`} className="flex gap-4 items-start">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-sm bg-berry/10 text-berry shrink-0 mt-0.5">
                  <Check size={14} />
                </span>
                <span className="text-slate-700 leading-relaxed">{o}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </section>

      <section className="border-t border-slate-200">
        <Link
          to={`/work/${next.id}`}
          data-testid="project-next"
          className="group block mx-auto max-w-5xl section-x py-14 flex items-center justify-between gap-6"
        >
          <div>
            <p className="label-caps text-slate-400 mb-2">Next project</p>
            <h3 className="font-serif text-2xl sm:text-3xl text-navy group-hover:text-berry transition-colors duration-200">
              {next.title}
            </h3>
          </div>
          <ArrowRight size={28} className="text-navy group-hover:text-berry group-hover:translate-x-1 transition-[transform,color] duration-200 shrink-0" />
        </Link>
      </section>

      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-5xl section-x py-16 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl">Have a similar challenge?</h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">Let's talk about how clearer financial insight can support your next decision.</p>
          <Link
            to="/"
            data-testid="project-cta-contact"
            onClick={() => setTimeout(() => document.getElementById("contact")?.scrollIntoView(), 100)}
            className="inline-block mt-8 bg-berry hover:bg-berry-light text-white px-8 py-3.5 rounded-sm transition-colors duration-200 hover:scale-[1.02]"
          >
            Get in touch
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
